import { ref } from 'vue';

export const useTutorial = () => {
  const stepTracker = ref(0);
  let currentSteps = [];
  let isActive = false;
  let cleanupCallback = null;
  let currentHighlightedElement = null;

  const onScroll = () => {
    if (!currentHighlightedElement) return;
    updateBackdropCutout(currentHighlightedElement.getBoundingClientRect());
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Poll for an element for a short window before giving up. Fields that only
  // mount after a preceding step toggles them on (v-if reveals) may not be in
  // the DOM the instant the next step runs, so we wait briefly for them.
  const waitForElement = async (selector, timeout = 600) => {
    const deadline = Date.now() + timeout;
    let element = document.querySelector(selector);
    while (!element && Date.now() < deadline) {
      await delay(50);
      element = document.querySelector(selector);
    }
    return element;
  };

  const removeHighlight = (element) => {
    if (!element) return;
    element.classList.remove('tutorial-highlight');
  };

  const BACKDROP_PANE_IDS = [
    'tutorial-backdrop-top',
    'tutorial-backdrop-bottom',
    'tutorial-backdrop-left',
    'tutorial-backdrop-right',
  ];

  const removeOverlay = () => {
    BACKDROP_PANE_IDS.forEach(id => document.getElementById(id)?.remove());
    document.getElementById('tutorial-tooltip')?.remove();
  };

  const updateBackdropCutout = (rect) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 4;
    const top    = Math.max(0, rect.top - pad);
    const bottom = Math.min(vh, rect.bottom + pad);
    const left   = Math.max(0, rect.left - pad);
    const right  = Math.min(vw, rect.right + pad);

    const positions = {
      'tutorial-backdrop-top':    { top: 0,      left: 0,     width: vw,         height: top },
      'tutorial-backdrop-bottom': { top: bottom,  left: 0,     width: vw,         height: vh - bottom },
      'tutorial-backdrop-left':   { top: top,     left: 0,     width: left,       height: bottom - top },
      'tutorial-backdrop-right':  { top: top,     left: right, width: vw - right, height: bottom - top },
    };

    for (const [id, pos] of Object.entries(positions)) {
      const el = document.getElementById(id);
      if (el) {
        el.style.top    = `${pos.top}px`;
        el.style.left   = `${pos.left}px`;
        el.style.width  = `${pos.width}px`;
        el.style.height = `${pos.height}px`;
      }
    }
  };

  const finishTutorial = () => {
    window.removeEventListener('scroll', onScroll);
    currentHighlightedElement = null;
    const currentElement = document.querySelector('.tutorial-highlight');
    if (currentElement) removeHighlight(currentElement);
    removeOverlay();
    isActive = false;
    stepTracker.value = 0;
    currentSteps = [];
    if (cleanupCallback) {
      cleanupCallback();
      cleanupCallback = null;
    }
  };

  const goToNextStep = async () => {
    if (stepTracker.value < currentSteps.length - 1) {
      stepTracker.value += 1;
      await executeStep(stepTracker.value);
    } else {
      finishTutorial();
    }
  };

  const goToPreviousStep = async () => {
    if (stepTracker.value > 0) {
      stepTracker.value -= 1;
      await executeStep(stepTracker.value);
    }
  };

  const createOverlay = () => {
    const newPanes = [];
    BACKDROP_PANE_IDS.forEach(id => {
      if (!document.getElementById(id)) {
        const pane = document.createElement('div');
        pane.id = id;
        pane.style.cssText = `
          position: fixed;
          z-index: 9998;
          background: rgba(0, 0, 0, 0.5);
          pointer-events: auto;
          opacity: 0;
          transition:
            opacity 0.3s ease,
            top 0.3s ease,
            left 0.3s ease,
            width 0.3s ease,
            height 0.3s ease;
        `;
        document.body.appendChild(pane);
        newPanes.push(pane);
      }
    });
    requestAnimationFrame(() => {
      newPanes.forEach(pane => { pane.style.opacity = '1'; });
    });

    const tooltip = document.createElement('div');
    tooltip.id = 'tutorial-tooltip';
    tooltip.classList.add('glass-card');
    tooltip.style.cssText = `
        position: absolute;
        border-radius: 0.5rem;
        z-index: 10000;
        max-width: 28rem;
        width: 100%;
        pointer-events: auto;
        opacity: 0;
        transform: translateY(6px);
        transition: all 0.5s ease-in-out;
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
    `;

    const header = document.createElement('div');
    header.style.cssText = `
        padding: 1rem 1.5rem;
        border-radius: 0.5rem 0.5rem 0 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    `;

    const title = document.createElement('h3');
    title.style.cssText = `
        font-size: 1.125rem;
        font-weight: 600;
        margin: 0;
    `;
    title.textContent = 'Tutorial';

    const rightSection = document.createElement('div');
    rightSection.style.cssText = `
        display: flex;
        align-items: center;
        gap: 0.5rem;
    `;

    const stepCounter = document.createElement('span');
    stepCounter.id = 'tutorial-step-counter';
    stepCounter.style.cssText = `
        font-size: 0.875rem;
        opacity: 0.75;
    `;

    const closeButton = document.createElement('button');
    closeButton.innerHTML = `
        <svg fill="currentColor" viewBox="0 0 20 20" style="width: 1.25rem; height: 1.25rem;">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
        </svg>
    `;
    closeButton.style.cssText = `
        color: #6b7280;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        display: flex;
        align-items: center;
    `;
    closeButton.addEventListener('mouseenter', () => { closeButton.style.color = '#374151'; });
    closeButton.addEventListener('mouseleave', () => { closeButton.style.color = '#6b7280'; });
    closeButton.onclick = finishTutorial;

    rightSection.appendChild(stepCounter);
    rightSection.appendChild(closeButton);
    header.appendChild(title);
    header.appendChild(rightSection);

    const content = document.createElement('div');
    content.id = 'tutorial-tooltip-content';
    content.style.cssText = `
        padding: 1.5rem;
        font-size: 0.875rem;
        line-height: 1.5;
    `;

    const footer = document.createElement('div');
    footer.style.cssText = `
        padding: 1rem 1.5rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
    `;

    const previousButton = document.createElement('button');
    previousButton.id = 'tutorial-previous-button';
    previousButton.textContent = 'Previous';
    previousButton.style.cssText = `
        padding: 0.5rem 1rem;
        background: #f3f4f6;
        border: none;
        border-radius: 0.375rem;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
    `;
    previousButton.classList.add('btn');
    previousButton.addEventListener('click', goToPreviousStep);

    const nextButton = document.createElement('button');
    nextButton.id = 'tutorial-next-button';
    nextButton.textContent = 'Next';
    nextButton.style.cssText = `
        padding: 0.5rem 1rem;
        background: #3b82f6;
        border: none;
        border-radius: 0.375rem;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        color: white;
    `;
    nextButton.classList.add('btn');
    nextButton.addEventListener('click', goToNextStep);

    footer.appendChild(previousButton);
    footer.appendChild(nextButton);

    tooltip.appendChild(header);
    tooltip.appendChild(content);
    tooltip.appendChild(footer);

    document.body.appendChild(tooltip);

    window.addEventListener('scroll', onScroll, { passive: true });

    return { tooltip };
  };

  const positionTooltip = (tooltip, rect) => {
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom + window.scrollY + 10;
    let left = rect.left + window.scrollX;

    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 20;
    }
    if (left < 0) {
      left = 20;
    }
    if (rect.bottom + tooltipRect.height + 10 > viewportHeight) {
      top = rect.top + window.scrollY - tooltipRect.height - 10;
    }
    if (top < window.scrollY) {
      top = rect.bottom + window.scrollY + 10;
    }

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  };

  const highlightElement = async (element, message, currentStep, totalSteps) => {
    if (!element) return;

    currentHighlightedElement = element;
    element.classList.add('tutorial-highlight');

    let tooltip = document.getElementById('tutorial-tooltip');
    let content = document.getElementById('tutorial-tooltip-content');
    let stepCounter = document.getElementById('tutorial-step-counter');

    if (!tooltip) {
      createOverlay();
      tooltip = document.getElementById('tutorial-tooltip');
      content = document.getElementById('tutorial-tooltip-content');
      stepCounter = document.getElementById('tutorial-step-counter');
    }

    if (content) content.textContent = message;
    if (stepCounter) stepCounter.textContent = `${currentStep} of ${totalSteps}`;

    const previousButton = document.getElementById('tutorial-previous-button');
    const nextButton = document.getElementById('tutorial-next-button');

    if (previousButton) {
      previousButton.disabled = currentStep === 1;
      previousButton.style.opacity = currentStep === 1 ? '0.5' : '1';
      previousButton.style.cursor = currentStep === 1 ? 'not-allowed' : 'pointer';
    }
    if (nextButton) {
      nextButton.textContent = currentStep === totalSteps ? 'Finish' : 'Next';
    }

    element.scrollIntoView({
      behavior: 'instant',
      block: 'center',
      inline: 'nearest'
    });

    if (tooltip) {
      // Defer positioning to next frame so the tooltip has real dimensions
      await new Promise(resolve => requestAnimationFrame(resolve));
      const rect = element.getBoundingClientRect();
      updateBackdropCutout(rect);
      positionTooltip(tooltip, rect);
      requestAnimationFrame(() => {
        tooltip.style.opacity = '1';
        tooltip.style.transform = 'translateY(0)';
      });
    }
  };

  const typeText = async (element, text) => {
    element.value = '';
    element.focus();
    for (let char of text) {
      element.value += char;
      element.dispatchEvent(new Event('input', { bubbles: true }));
      await delay(100);
    }
  };

  const selectOption = async (selectElement, value) => {
    if (selectElement.tagName === 'SELECT') {
      selectElement.focus();
      selectElement.value = value;
      selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      selectElement.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      selectElement.dispatchEvent(new CustomEvent('tutorial:select', { detail: { value } }));
    }
    await delay(200);
  };

  const executeStep = async (stepIndex) => {
    if (stepIndex < 0 || stepIndex >= currentSteps.length) return;

    const step = currentSteps[stepIndex];
    const element = await waitForElement(step.element);

    if (!element) {
      if (stepIndex < currentSteps.length - 1) {
        stepTracker.value += 1;
        await executeStep(stepTracker.value);
      } else {
        finishTutorial();
      }
      return;
    }

    const previousElement = document.querySelector('.tutorial-highlight');
    if (previousElement) removeHighlight(previousElement);

    await highlightElement(element, step.message, stepIndex + 1, currentSteps.length);

    await delay(1000);

    if (step.action === 'type') {
      await typeText(element, step.value);
    } else if (step.action === 'checkbox') {
      const input = element.control ?? document.getElementById(element.htmlFor);
      const isChecked = input ? input.checked : element.getAttribute('aria-checked') === 'true';
      if (step.value === undefined || isChecked !== Boolean(step.value)) {
        element.click();
      }
    } else if (step.action === 'click') {
      element.click();
    } else if (step.action === 'select') {
      await selectOption(element, step.value);
    } else if (step.action === 'display') {
      await delay(2000);
    }
  };

  const start = async (steps, onFinish) => {
    if (isActive) return false;
    currentSteps = steps;
    stepTracker.value = 0;
    isActive = true;
    cleanupCallback = onFinish;
    await executeStep(stepTracker.value);
    return true;
  };

  return { start };
};
